import { useState, useMemo, useRef, useEffect } from 'react'
import { Link } from '@reach/router'
import { useTranslation } from 'react-i18next'
import helpers from '../../../helpers'
import useUser from 'hooks/useUser'
import useQuery from 'hooks/useQuery'
import useMutation from 'hooks/useMutation'
import { ArcaStatusIndicator } from 'Components/ArcaStatusIndicator/ArcaStatusIndicator'
import { rejectionReason } from 'utils/arcaRejectionReason'
import { TrialInfoPopover } from 'Components/TrialInfoPopover/TrialInfoPopover'
import { BetaFeedback } from 'Components/BetaFeedback/BetaFeedback'

// Mismo criterio que Events/Agenda/Event/Facturar.js (pedido de Nacho,
// 2026-08-05): mostrar el email real (o afirmar con seguridad que no hay
// ninguno) en vez de un condicional vago — es un dato que ya tenemos, no
// algo que el profesional tenga que adivinar.
//
// Bug real (2026-08-05, mismo hallazgo que Events/Agenda/Event/Facturar.js):
// el consumer embebido en un turno lleva el email en el campo SINGULAR
// `email` (string) — mismo que usa el backend para notificaciones reales del
// turno — no en `emails` (array, el shape del documento completo del
// consumer). Estas sesiones vienen de eventsQuery (GET /:consumerID/events,
// docs de turnos crudos), así que sufren el mismo problema.
const consumerEmail = consumer => {
  const emails = [
    ...(consumer?.email ? [consumer.email] : []),
    ...(consumer?.emails || []).map(item => (typeof item === 'string' ? item : item?.value)),
  ]
    .map(helpers.getEmail)
    .filter(helpers.isEmail)
  return emails[0] || null
}

/**
 * Facturar en lote las sesiones de un paciente (§negocio, 2026-08-04) — caso
 * real: un psicólogo factura de una vez todas las sesiones del mes de un
 * paciente, con cada fecha en el detalle (para el reintegro, §6.9). Ruta
 * propia (`:consumerID/facturar/*`, ver Consumers.js), no un modal — la
 * lista de sesiones necesita más lugar que lo que entra en el modal de
 * Facturar.js (turno individual).
 *
 * Alcance v1 (simplificaciones deliberadas, no descuidos — ver
 * ARCA_FACTURACION_PLAN.md):
 *   - Solo las sesiones del PROPIO profesional (user.team?.resourceID) — no
 *     hay selector de identidad delegada acá (a diferencia de Facturar.js).
 *   - El monto se edita como un solo número para todo el lote (no por sesión).
 *   - Sin sugerencia de precarga por historial, sin restauración de una
 *     factura previa, sin compartir por WhatsApp — el turno individual
 *     (Facturar.js) sigue siendo el camino para eso.
 */

const CONDICIONES_IVA = [
  { id: 5, label: 'Consumidor Final' },
  { id: 4, label: 'IVA Sujeto Exento' },
  { id: 1, label: 'IVA Responsable Inscripto' },
  { id: 6, label: 'Responsable Monotributo' },
  { id: 15, label: 'IVA No Alcanzado' },
]

const DOC_TIPOS = [
  { id: 99, label: 'Sin identificar (Consumidor Final)' },
  { id: 96, label: 'DNI' },
  { id: 80, label: 'CUIT' },
  { id: 86, label: 'CUIL' },
]

// Mismo criterio que Events/Agenda/Event/Facturar.js (§3.3) — condiciones IVA
// del receptor que exigen Factura A, la única que va SIEMPRE con CUIT.
const FACTURA_A_CONDICIONES = [1, 6, 13, 16]

// Mismo tope que el backend (arca/emit.js#MAX_BATCH_EVENTS) — preselección
// acotada a las más recientes, nunca a todo el historial sin facturar.
const MAX_BATCH_PRESELECT = 60

const MODALIDADES_FISCALES = [
  { id: 'particular', label: 'Particular' },
  { id: 'obra_social_obligatoria', label: 'Obra social — afiliación obligatoria' },
  { id: 'obra_social_voluntaria', label: 'Obra social — afiliación voluntaria' },
  { id: 'prepaga', label: 'Prepaga (reintegro al paciente)' },
]

// Mismo mapeo que Facturar.js/Arca.js/ArcaGrants.js — useMutation tira
// Error(data.error), el código crudo del backend. Duplicado a propósito
// (mismo criterio ya establecido en el resto de los componentes ARCA).
const ERROR_LABEL = {
  EMIT_NOT_ALLOWED: 'No estás autorizado a emitir facturas para esta identidad fiscal',
  NO_FISCAL_IDENTITY: 'El profesional no tiene la vinculación ARCA configurada',
  LINK_NOT_ACTIVE: 'La vinculación ARCA no está activa',
  INVALID_CUIT: 'La vinculación tiene un CUIT inválido — contactá a soporte',
  NO_PUNTO_VENTA: 'No hay puntos de venta Web Services habilitados',
  INVALID_AMOUNT: 'El lote no tiene un monto válido para facturar',
  RECEPTOR_REQUIRED: 'Falta el nombre del receptor de la factura',
  RECEPTOR_DOC_REQUIRED: 'Falta el número de documento del receptor',
  RECEPTOR_DOC_INVALID: 'El documento del receptor es inválido',
  RECEPTOR_CUIT_REQUERIDO_FACTURA_A: 'Factura A requiere el CUIT del receptor (no DNI ni consumidor final)',
  INVALID_CONDICION_IVA: 'La condición IVA del receptor es inválida',
  MODALIDAD_FISCAL_REQUIRED: 'Como Responsable Inscripto, tenés que indicar la modalidad de cobro',
  INVALID_DESCRIPCION: 'La descripción es demasiado larga (máx. 300 caracteres)',
  EVENT_NOT_FOUND: 'Uno de los turnos ya no existe o no pertenece a este equipo — actualizá la vista',
  EVENT_WITHOUT_RESOURCE: 'Un turno no tiene profesional asignado',
  BATCH_REQUIRES_MULTIPLE_EVENTS: 'Elegí al menos 2 sesiones — para una sola, facturala desde el turno',
  BATCH_TOO_LARGE: 'Elegiste demasiadas sesiones para un solo lote',
  BATCH_MIXED_RESOURCE: 'Las sesiones elegidas son de distintos profesionales',
  BATCH_MIXED_CONSUMER: 'Las sesiones elegidas son de distintos pacientes',
  BATCH_MISSING_DATES: 'Una de las sesiones no tiene fecha',
  INVOICE_EXISTS: 'Una de las sesiones ya tiene una factura emitida o en curso — actualizá la vista',
  not_found: 'Esta funcionalidad ya no está disponible para tu equipo — actualizá la página',
}
const errorLabel = error => ERROR_LABEL[error?.message] || error?.message || 'Error inesperado'

const formatAmount = value => Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Mismo parser robusto que Facturar.js (bug real ya corregido ahí: un monto
// tipeado como "15.000" no puede interpretarse como 15) — duplicado acá por
// el mismo motivo que el resto de los helpers de este módulo.
const GROUP3_DOT_RE = /^\d{1,3}(\.\d{3})+$/
const GROUP3_COMMA_RE = /^\d{1,3}(,\d{3})+$/
const parseAmountInput = raw => {
  const str = String(raw ?? '').trim()
  const lastComma = str.lastIndexOf(',')
  const lastDot = str.lastIndexOf('.')
  if (lastComma !== -1 && lastDot !== -1) {
    return lastComma > lastDot ? Number(str.replace(/\./g, '').replace(',', '.')) : Number(str.replace(/,/g, ''))
  }
  if (lastComma !== -1) return Number(GROUP3_COMMA_RE.test(str) ? str.replace(/,/g, '') : str.replace(',', '.'))
  if (GROUP3_DOT_RE.test(str)) return Number(str.replace(/\./g, ''))
  return Number(str)
}

const toArgDateInput = epochMs =>
  new Intl.DateTimeFormat('fr-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date(Number(epochMs)))
const formatArgDateDisplay = epochMs =>
  new Date(Number(epochMs)).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })

export function Facturar({ consumerID, location }) {
  // Prefill por query (?descripcion=&amount=): lo usa el odontograma para
  // llegar acá con los tratamientos realizados ya detallados y sumados. No
  // reemplaza la elección de sesiones (la factura sigue colgando de los
  // turnos), solo evita que el profesional retipee lo que el sistema ya sabe.
  const prefill = useMemo(() => {
    try {
      const params = new URLSearchParams(location?.search || window.location.search || '')
      const descripcion = (params.get('descripcion') || '').slice(0, 300).trim()
      const amountRaw = (params.get('amount') || '').trim()
      const amount = /^\d+(\.\d{1,2})?$/.test(amountRaw) ? amountRaw : ''
      return { descripcion, amount }
    } catch { return { descripcion: '', amount: '' } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { t } = useTranslation()
  const user = useUser()
  const resourceId = user.team?.resourceID

  const [selected, setSelected] = useState(() => new Set())
  const [receptor, setReceptor] = useState({ label: '', docTipo: 99, docNro: '' })
  const [condicionIva, setCondicionIva] = useState(5)
  const [modalidadFiscal, setModalidadFiscal] = useState('particular')
  const [amount, setAmount] = useState(prefill.amount)
  const [descripcion, setDescripcion] = useState(prefill.descripcion)
  // Un prefill cuenta como "tocado": si no, el useEffect de los defaults lo
  // pisaría con la descripción de las sesiones en el primer render.
  const [amountTouched, setAmountTouched] = useState(!!prefill.amount)
  const [descripcionTouched, setDescripcionTouched] = useState(!!prefill.descripcion)
  const [step, setStep] = useState('form') // 'form' | 'polling' | 'queued-long' | 'done' | 'failed'
  const [error, setError] = useState(null)
  const [invoice, setInvoice] = useState(null)
  const pollStartedAt = useRef(null)
  const defaultsApplied = useRef(false)
  // Capturado en onSubmit (no derivado en cada render de `candidates`, que
  // puede vaciarse o reordenarse después de emitir — un refetch en
  // background ya podría no incluir estos turnos si el worker los marcó
  // rápido) — así el mensaje post-emisión sigue mostrando el email correcto
  // aunque `candidates` cambie mientras se está en el paso "done".
  const sentToEmailRef = useRef(null)
  // Turnos de crontu (§negocio, 2026-08-05, pedido de Nacho): el backend
  // (deliver()) entrega por el canal propio de crontu, no por email directo
  // — mismo criterio de "principal" que usa el backend para un lote
  // (arca/emit.js#createBatchInvoice: invoice.event_id = eventIds[0]).
  const sentViaCrontuRef = useRef(false)
  const feedbackRef = useRef(null)

  const enabledQuery = useQuery(window._l('/arca/enabled').substr(1), { enabled: !!resourceId, staleTime: 30000 })
  const arcaEnabled = !!enabledQuery.data?.enabled
  // Bug real (UX, encontrado probando en QA, 2026-08-05): a diferencia del
  // modal de turno individual (Events/Agenda/Event/Facturar.js), esta
  // página nunca avisaba si ARCA está caído — mismo patrón "hermano sin el
  // mismo tratamiento" ya encontrado antes en Arca.js (drmanager). El
  // profesional armaba un lote entero sin ningún indicio de que la emisión
  // iba a quedar en cola esperando a que ARCA se recupere.
  const healthQuery = useQuery(window._l('/arca/health').substr(1), { enabled: arcaEnabled, staleTime: 30000 })
  const arcaDown = healthQuery.data?.up === false
  const identitiesQuery = useQuery(window._l(`/arca/${resourceId}/identities`).substr(1), { enabled: !!resourceId && arcaEnabled })
  const eventsQuery = useQuery(window._l(`/${consumerID}/events`).substr(1), {
    initialData: { past: [], next: [] },
    enabled: !!consumerID && !!resourceId && arcaEnabled,
  })

  const identities = (identitiesQuery.data?.identities || []).filter(item => item.canEmit)
  const identity = identities[0] // sin selector de identidad delegada en v1 (ver docblock)
  const esResponsableInscripto = identity?.condicionFiscal === 'responsable_inscripto'

  // Candidatas: propias del profesional, sin factura activa todavía, con
  // fecha — "pagado" NO es un filtro (mismo criterio ya corregido en
  // Event.js/Facturar.js: un turno creado a mano puede no tener pricing).
  const candidates = useMemo(() => {
    const all = [...(eventsQuery.data?.past || []), ...(eventsQuery.data?.next || [])]
    return all
      .filter(event => event.resource?.id === resourceId && !event.invoice && event.startsAt)
      .sort((a, b) => a.startsAt - b.startsAt)
  }, [eventsQuery.data, resourceId])

  // Preselecciona la primera vez que llegan — el caso común (facturar el mes
  // completo) no debería empezar con la lista vacía; el profesional puede
  // deseleccionar lo que no quiera incluir.
  //
  // Bug real (UX, 2026-08-05): preseleccionaba TODAS las candidatas sin
  // ningún tope — un paciente con meses/años de sesiones nunca facturadas
  // (ej. arrancó a usar ARCA mucho después de empezar el tratamiento) armaba
  // de entrada un lote gigante y sorpresivo, que además podía superar
  // MAX_BATCH_EVENTS (60) del backend y fallar con BATCH_TOO_LARGE recién al
  // confirmar. `candidates` viene ordenado ascendente por fecha — el tail son
  // las más recientes.
  useEffect(() => {
    if (defaultsApplied.current || candidates.length === 0) return
    defaultsApplied.current = true
    setSelected(new Set(candidates.slice(-MAX_BATCH_PRESELECT).map(event => event.id)))
    // Mismo criterio que la factura individual: si el paciente ya tiene DNI
    // cargado en su ficha, precargarlo (docTipo 96) en vez de dejarlo en
    // "Sin identificar" con el input deshabilitado.
    const consumerDni = candidates[0].consumer?.identification || ''
    setReceptor({ label: candidates[0].consumer?.label || '', docTipo: consumerDni ? 96 : 99, docNro: consumerDni })
  }, [candidates])

  const selectedEvents = candidates.filter(event => selected.has(event.id))
  const defaultTotal = selectedEvents.reduce((sum, event) => sum + Number(event.pricing?.total || 0), 0)
  const dates = selectedEvents.map(event => event.startsAt).sort((a, b) => a - b)

  // Bug real (2026-08-04, mismo hallazgo que emit.js#resolveBatchDescripcion
  // en el backend): enumerar cada fecha sin límite supera los 300 caracteres
  // que el backend acepta mucho antes de llegar a un lote grande (~24
  // sesiones ya alcanza) — sin este fallback, el profesional llenaba el
  // picker con un mes de sesiones diarias y recién se enteraba del error al
  // confirmar. Mismo resumen compacto (cantidad + rango) que el backend, así
  // el preview que ve acá coincide con lo que realmente se va a facturar.
  const defaultDescripcion = useMemo(() => {
    if (dates.length === 0) return ''
    const formatted = dates.map(formatArgDateDisplay)
    if (formatted.length === 1) return `Sesión de consulta — ${formatted[0]}`
    const last = formatted[formatted.length - 1]
    const enumerated = `Sesiones de consulta — ${formatted.slice(0, -1).join(', ')} y ${last}`
    if (enumerated.length <= 300) return enumerated
    return `Sesiones de consulta — ${formatted.length} sesiones entre ${formatted[0]} y ${last}`
  }, [dates]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!amountTouched) setAmount(defaultTotal ? String(defaultTotal) : '') }, [defaultTotal, amountTouched])
  useEffect(() => { if (!descripcionTouched) setDescripcion(defaultDescripcion) }, [defaultDescripcion, descripcionTouched])

  const toggleEvent = eventId => {
    setSelected(current => {
      const next = new Set(current)
      if (next.has(eventId)) next.delete(eventId); else next.add(eventId)
      return next
    })
  }

  const pollQuery = useQuery(window._l(`/arca/invoices/${invoice?.id}`).substr(1), {
    enabled: step === 'polling' && !!invoice?.id,
    refetchInterval: step === 'polling' ? 2500 : false,
  })

  useEffect(() => {
    if (step !== 'polling') return
    if (!pollStartedAt.current) pollStartedAt.current = Date.now()
    const current = pollQuery.data?.invoice
    if (current) {
      setInvoice(current)
      if (current.status === 'rejected' || current.status === 'failed') {
        pollStartedAt.current = null
        setStep('failed')
        return
      }
      if (current.status === 'approved' && current.pdf) {
        pollStartedAt.current = null
        setStep('done')
        return
      }
    }
    if (Date.now() - pollStartedAt.current > 30000) {
      pollStartedAt.current = null
      setStep('queued-long')
    }
  }, [step, pollQuery.data, pollQuery.dataUpdatedAt, pollQuery.errorUpdatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  const [mutateEmit] = useMutation(window._l('/arca/invoices/batch').substr(1), {
    onSuccess: payload => {
      if (payload?.invoice?.id) {
        setInvoice(payload.invoice)
        setStep('polling')
      } else {
        setError(ERROR_LABEL[payload?.error] || payload?.message || 'Error inesperado')
        setStep('form')
      }
    },
    onError: mutationError => {
      setError(errorLabel(mutationError))
      setStep('form')
    },
  })

  if (!resourceId) return null
  if (!arcaEnabled) return null

  const onSubmit = () => {
    if (selectedEvents.length < 2) {
      setError(t('arca_batch_min_events', 'Elegí al menos 2 sesiones — para una sola, facturala desde el turno'))
      return
    }
    if (!receptor.label.trim()) {
      setError(t('arca_receptor_required', 'Falta el nombre del receptor de la factura'))
      return
    }
    const docTipoNum = Number(receptor.docTipo)
    const docNroDigits = String(receptor.docNro || '').replace(/\D/g, '')
    if (docTipoNum !== 99 && !docNroDigits) {
      setError(t('arca_receptor_doc_required', 'Falta el número de documento del receptor'))
      return
    }
    // Bug real (UX): a diferencia de Events/Agenda/Event/Facturar.js (turno
    // individual), esta página no tenía estas dos validaciones — un typo en
    // el CUIT/CUIL o una condición IVA de Factura A sin CUIT cargado solo se
    // enteraba al rechazo del backend, después de armar todo el lote.
    if ((docTipoNum === 80 || docTipoNum === 86) && docNroDigits.length !== 11) {
      setError(t('arca_receptor_doc_invalid_length', `El ${docTipoNum === 80 ? 'CUIT' : 'CUIL'} del receptor debe tener 11 dígitos`))
      return
    }
    const parsedAmount = parseAmountInput(amount)
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError(t('arca_amount_invalid', 'El monto a facturar debe ser mayor a cero'))
      return
    }
    if (esResponsableInscripto && FACTURA_A_CONDICIONES.includes(Number(condicionIva)) && docTipoNum !== 80) {
      setError(t('arca_receptor_cuit_requerido', 'Para facturar a un Responsable Inscripto/Monotributista (Factura A) necesitás el CUIT del receptor — cambiá el documento a CUIT o elegí otra condición IVA'))
      return
    }
    setError(null)
    setStep('polling-start')
    sentToEmailRef.current = consumerEmail(selectedEvents[0]?.consumer)
    sentViaCrontuRef.current = selectedEvents[0]?.source === 'crontu'
    mutateEmit({
      eventIds: selectedEvents.map(event => event.id),
      amount: parsedAmount,
      fiscalResourceId: identity?.fiscalResourceId,
      receptor: { label: receptor.label, docTipo: docTipoNum, docNro: receptor.docNro },
      condicionIvaReceptorId: Number(condicionIva),
      modalidadFiscal: esResponsableInscripto ? modalidadFiscal : undefined,
      descripcion: descripcion.trim() || undefined,
    })
  }

  return (
    <div className="container p-4 my-4 shadow bg-white rounded" style={{ maxWidth: '640px' }}>
      {/* Bug real (auditoría 2026-08-17, responsive): a 375px este container
          da ~327px de ancho de contenido — el span izquierdo (h5 + dot +
          badge) necesita ~230-250px y el botón "Volver" otros ~70-90px, total
          justo en el límite o por encima, con justify-content-between y sin
          flex-wrap como red de contención. Mismo mecanismo ya corregido en
          Arca.js (misma auditoría) — acá agrega gap-2 porque, a diferencia de
          Arca.js, esta fila no tenía ningún gap entre el span y "Volver". */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <span className="d-flex align-items-center gap-2">
          <h5 className="mb-0">{t('arca_batch_title', 'Facturar varias sesiones')}</h5>
          {arcaEnabled && <ArcaStatusIndicator />}
          {arcaEnabled && (
            <TrialInfoPopover
              title={t('arca_title', 'Facturación ARCA')}
              description={t('arca_trial_description', 'Emití comprobantes electrónicos habilitados por AFIP/ARCA directo desde DrApp — sin cambiar de sistema para facturar cada turno.')}
              onLeaveReview={() => feedbackRef.current?.openManual()}
            />
          )}
        </span>
        <Link to="../" className="btn btn-link btn-sm">{t('back', 'Volver')}</Link>
      </div>

      {/* Montado SIEMPRE (no solo si invoice?.status === 'approved') — sin
          esto, clickear el badge ANTES de facturar no hace nada
          (feedbackRef.current sigue en null). Mismo criterio que
          Events/Agenda/Event/Facturar.js: sin solapa fija propia (ya no
          existe showTab), el disparador voluntario es el badge de arriba.
          autoTriggerSignal SÍ sigue gateado por invoice?.status ===
          'approved' (no por invoice?.id solo): invoice?.id ya existe desde
          el 202 inicial de la emisión (status 'queued'/'processing', antes
          del CAE) — mismo motivo, mismo fix, que el archivo individual. */}
      {/* featureName reusa la misma key que Arca.js/ArcaBilling.js
          ("Facturación ARCA"), no el título local de esta pantalla
          ("Facturar varias sesiones") — un solo nombre reconocible en las
          4 pantallas que usan esta feature. */}
      <BetaFeedback ref={feedbackRef} featureKey="arca" featureName={t('arca_title', 'Facturación ARCA')} autoTriggerSignal={invoice?.status === 'approved' ? invoice?.id : null} />

      {arcaDown && (
        <div className="alert alert-warning py-2 small">
          {t('arca_down_warning', 'ARCA está con problemas en este momento — tu factura va a quedar en cola y se emitirá apenas se recupere.')}
        </div>
      )}

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Bug real (UX, 2026-08-05): un error de red/backend en cualquiera de
          las dos queries (identitiesQuery/eventsQuery) caía en el mismo
          branch que "0 resultados" — el profesional veía "no tenés
          vinculación ARCA" o "el paciente no tiene sesiones" ante un fetch
          que simplemente falló, sin ninguna pista de que era transitorio ni
          forma de reintentar sin recargar toda la página. Mismo patrón que
          Events/Agenda/Event/Facturar.js#arca_modal_load_error. */}
      {(identitiesQuery.isError || eventsQuery.isError) && (
        <div className="alert alert-danger small">
          {t('arca_batch_load_error', 'No pudimos cargar el estado de facturación — revisá tu conexión.')}
          <button
            className="btn btn-sm btn-link p-0 ms-2"
            onClick={() => { identitiesQuery.refetch(); eventsQuery.refetch() }}
          >
            {t('arca_retry_load', 'Reintentar')}
          </button>
        </div>
      )}

      {step === 'form' && !identitiesQuery.isError && !eventsQuery.isError && (
        <>
          {(eventsQuery.isLoading || identitiesQuery.isLoading) ? (
            <div className="text-center my-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
          ) : identities.length === 0 ? (
            <div className="alert alert-warning small">
              {t('arca_no_identity', 'No tenés una vinculación ARCA activa para emitir.')}
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-muted small">
              {t('arca_batch_no_candidates', 'Este paciente no tiene sesiones tuyas sin facturar.')}
            </p>
          ) : (
            <>
              {/* Card ARCA V2 (feedback de instancia): el aviso de
                  irreversibilidad vivía al final del form, después de cargar
                  todos los datos — el profesional lo veía recién cuando ya
                  había completado todo, demasiado tarde para que pese en la
                  decisión. Movido arriba de "Factura a nombre de", lo primero
                  que se ve al abrir el form de emisión. */}
              <div className="alert alert-light border py-2 small mb-3">
                {t('arca_confirm_note', 'Una vez emitida, la factura no se puede editar ni borrar — solo se corrige con una Nota de Crédito.')}
              </div>

              <p className="small text-muted mb-2">
                {t('arca_batch_hint', 'Elegí las sesiones a incluir en un solo comprobante — cada fecha queda en el detalle, para el reintegro.')}
              </p>
              {candidates.length > MAX_BATCH_PRESELECT && (
                <p className="small text-muted mb-2">
                  {t('arca_batch_old_sessions_hint', 'Hay {{count}} sesiones sin facturar más antiguas que las preseleccionadas — desplazate en la lista para incluirlas a mano si corresponde.', { count: candidates.length - MAX_BATCH_PRESELECT })}
                </p>
              )}
              <div className="border rounded mb-3" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {candidates.map(event => (
                  <label key={event.id} className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom small" style={{ cursor: 'pointer' }}>
                    <span className="d-flex align-items-center gap-2">
                      <input type="checkbox" className="form-check-input m-0" checked={selected.has(event.id)} onChange={() => toggleEvent(event.id)} />
                      {formatArgDateDisplay(event.startsAt)}
                    </span>
                    <span>{event.pricing?.total != null ? `$ ${formatAmount(event.pricing.total)}` : t('arca_batch_no_price', 'sin monto')}</span>
                  </label>
                ))}
              </div>

              <div className="mb-2">
                <label className="form-label small mb-1">{t('arca_receptor', 'Factura a nombre de')}</label>
                <input className="form-control form-control-sm" value={receptor.label}
                  onChange={event_ => setReceptor({ ...receptor, label: event_.target.value })} />
              </div>
              <div className="row g-2 mb-2">
                <div className="col-7">
                  <label className="form-label small mb-1">{t('arca_doc_tipo', 'Documento')}</label>
                  <select className="form-select form-select-sm" value={receptor.docTipo}
                    onChange={event_ => setReceptor({ ...receptor, docTipo: event_.target.value, docNro: Number(event_.target.value) === 99 ? '' : receptor.docNro })}>
                    {DOC_TIPOS.map(doc => <option key={doc.id} value={doc.id}>{doc.label}</option>)}
                  </select>
                </div>
                <div className="col-5">
                  <label className="form-label small mb-1">{t('arca_doc_nro', 'Número')}</label>
                  <input className="form-control form-control-sm" value={receptor.docNro} disabled={Number(receptor.docTipo) === 99}
                    onChange={event_ => setReceptor({ ...receptor, docNro: event_.target.value })} />
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label small mb-1">{t('arca_condicion_iva', 'Condición frente al IVA')}</label>
                <select className="form-select form-select-sm" value={condicionIva} onChange={event_ => setCondicionIva(event_.target.value)}>
                  {CONDICIONES_IVA.map(condicion => <option key={condicion.id} value={condicion.id}>{condicion.label}</option>)}
                </select>
              </div>
              {esResponsableInscripto && (
                <div className="mb-2">
                  <label className="form-label small mb-1">{t('arca_modalidad_fiscal', 'Modalidad de cobro')}</label>
                  <select className="form-select form-select-sm" value={modalidadFiscal} onChange={event_ => setModalidadFiscal(event_.target.value)}>
                    {MODALIDADES_FISCALES.map(modalidad => <option key={modalidad.id} value={modalidad.id}>{modalidad.label}</option>)}
                  </select>
                </div>
              )}
              <div className="mb-2">
                <label className="form-label small mb-1">{t('arca_descripcion', 'Detalle (visible en la factura)')}</label>
                <textarea className="form-control form-control-sm" rows="2" maxLength="300" value={descripcion}
                  onChange={event_ => { setDescripcionTouched(true); setDescripcion(event_.target.value) }} />
              </div>
              <div className="mb-3" style={{ maxWidth: '200px' }}>
                <label className="form-label small mb-1">{t('arca_amount', 'Monto total del lote')}</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">$</span>
                  <input className="form-control" inputMode="decimal" value={amount}
                    onChange={event_ => { setAmountTouched(true); setAmount(event_.target.value) }} />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-sm btn-primary" onClick={onSubmit} disabled={selectedEvents.length < 2}>
                  {t('arca_batch_emit_confirm', 'Emitir factura de {{count}} sesiones', { count: selectedEvents.length })}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {(step === 'polling' || step === 'polling-start') && (
        <div className="text-center my-3">
          <div className="spinner-border spinner-border-sm text-primary me-2" />
          <span className="small">{t('arca_emitting', 'Emitiendo comprobante en ARCA...')}</span>
        </div>
      )}

      {step === 'queued-long' && (
        <div className="alert alert-warning small">
          {invoice?.status === 'approved'
            ? t('arca_queued_long_pdf', 'El comprobante ya se aprobó — CAE {{cae}}. Estamos generando el PDF, puede demorar un poco más.', { cae: invoice.cae })
            : t('arca_queued_long', 'La emisión sigue en cola — no hace falta que esperes, se emite y envía automáticamente.')}
        </div>
      )}

      {step === 'done' && invoice && (
        <div className="alert alert-success small">
          <b>{t('arca_emitted_ok', 'Comprobante emitido')}</b> — CAE {invoice.cae}
          <div className="mt-2 d-flex gap-2">
            {invoice.pdf && <a className="btn btn-sm btn-outline-primary" href={invoice.pdf} target="_blank" rel="noopener noreferrer">PDF</a>}
            <Link to="../" className="btn btn-sm btn-outline-secondary">{t('back', 'Volver')}</Link>
          </div>
          <div className="text-muted mt-1">
            {sentViaCrontuRef.current
              ? t('arca_sent_via_crontu', 'Se envía al paciente por el sistema de turnos (crontu) — no por email directo de DrApp.')
              : sentToEmailRef.current
                ? t('arca_email_sent', 'Se envía automáticamente a {{email}}.', { email: sentToEmailRef.current })
                : t('arca_no_email', 'El paciente no tiene ningún email cargado — descargá el PDF de arriba para compartirlo.')}
          </div>
          {/* Bug real: faltaba, a diferencia de la individual (Events/Agenda/
              Event/Facturar.js) — importa más ACÁ que ahí, porque el caso de
              uso principal del lote es el reintegro (el paciente suele
              necesitar el PDF semanas después para presentarlo ante la
              prepaga/obra social) y sin email cargado el link es la ÚNICA
              vía, sin ningún aviso de que va a dejar de funcionar. */}
          <div className="text-muted mt-1">{t('arca_pdf_link_expires', 'El link al PDF vence en 7 días — el adjunto del email no.')}</div>
        </div>
      )}

      {step === 'failed' && invoice && (
        <div className="alert alert-danger small">
          <b>{t('arca_rejected', 'ARCA rechazó el comprobante')}</b>
          {/* Bug real: a diferencia de la individual (Events/Agenda/Event/
              Facturar.js), acá no se usaba rejectionReason — cualquier
              código clasificado (CUIT_NOT_DELEGATED, PUNTO_VENTA_NO_RECE,
              RETRIES_EXHAUSTED, RG4444) quedaba sin explicación, justo en
              el lote — donde el monto TOTAL de varias sesiones cruza el
              umbral de RG4444 con más frecuencia que una sola sesión, así
              que es más probable pisarlo justo en el flujo que menos lo
              explicaba. Mismo helper compartido con el historial. */}
          {rejectionReason(invoice) && <div className="mt-1">{rejectionReason(invoice)}</div>}
          {invoice.error?.code === 'RECEPTOR_REQUERIDO_RG4444' && (
            <div className="mt-1">{t('arca_rg4444_hint', 'El monto supera el umbral que exige identificar al receptor — cargá el DNI del paciente y volvé a intentar.')}</div>
          )}
          <button className="btn btn-sm btn-outline-primary mt-2 d-block" onClick={() => { setStep('form'); setInvoice(null) }}>
            {t('arca_retry', 'Corregir y reintentar')}
          </button>
        </div>
      )}
    </div>
  )
}

export default Facturar
